// batch-65 inkoop-kosten (FO-483) — inkoopkosten toerekenen aan een klant,
// werkbon of project, en doorbelasten naar de verkoopfactuur.
//
// Twee stappen (spec 183 A1): TOEREKENEN (de kost hoort bij een klant; een
// toewijzing KLANT/WERKBON/PROJECT op de inkoopregel) en optioneel DOORBELASTEN
// (een regel op de verkoopfactuur of het werkbonmateriaal, met opslag). Gedeelde
// Zod-schema's (API .safeParse + client-side prevalidatie, één bron) en DTO's.
// Geld en opslag in de DB als Decimal; opslag als fractie (0.10 = 10%).

import { z } from 'zod'
import { idParam } from './id-validators'
import {
  KLANT_TOEWIJZING_DOELTYPE,
  type KlantToewijzingDoeltype,
  type ToewijzingDoeltype,
} from './kostendrager'

// ─── Statussen en redenen (spiegelen de Prisma-enums) ────────────────────────

/** Doorbelast-status van een inkoopregel; null = geen klant-toerekening (n.v.t.). */
export const INKOOP_DOORBELAST_STATUS = ['NIET', 'TE_DOORBELASTEN', 'DOORBELAST'] as const
export type InkoopDoorbelastStatus = (typeof INKOOP_DOORBELAST_STATUS)[number]

export const INKOOP_DOORBELAST_STATUS_LABELS: Record<InkoopDoorbelastStatus, string> = {
  NIET: 'Niet doorbelasten',
  TE_DOORBELASTEN: 'Te doorbelasten',
  DOORBELAST: 'Doorbelast',
}

export const NIET_DOORBELASTEN_REDEN = [
  'GARANTIE',
  'IN_TARIEF_INBEGREPEN',
  'EIGEN_KOSTEN',
  'KLANT_LEVERT_ZELF',
  'OVERIG',
] as const
export type NietDoorbelastenReden = (typeof NIET_DOORBELASTEN_REDEN)[number]

export const NIET_DOORBELASTEN_REDEN_LABELS: Record<NietDoorbelastenReden, string> = {
  GARANTIE: 'Garantie',
  IN_TARIEF_INBEGREPEN: 'In het tarief inbegrepen',
  EIGEN_KOSTEN: 'Eigen kosten van STREEP',
  KLANT_LEVERT_ZELF: 'Klant regelt het zelf',
  OVERIG: 'Overig',
}

export const INKOOP_DOORBELASTING_ROUTE = ['CONCEPT_FACTUUR', 'WERKBON_MATERIAAL'] as const
export type InkoopDoorbelastingRoute = (typeof INKOOP_DOORBELASTING_ROUTE)[number]

export const INKOOP_DOORBELASTING_ROUTE_LABELS: Record<InkoopDoorbelastingRoute, string> = {
  CONCEPT_FACTUUR: 'Verkoopfactuur',
  WERKBON_MATERIAAL: 'Werkbonmateriaal',
}

export const INKOOP_DOORBELASTING_STATUS = ['ACTIEF', 'TERUGGEKOPPELD'] as const
export type InkoopDoorbelastingStatus = (typeof INKOOP_DOORBELASTING_STATUS)[number]

export const INKOOP_DOORBELASTING_TERUGKOPPEL_REDEN = [
  'VERKOOPFACTUUR_VERWIJDERD',
  'VERKOOPFACTUUR_GECREDITEERD',
  'VERKOOPREGEL_VERWIJDERD',
  'HANDMATIG',
] as const
export type InkoopDoorbelastingTerugkoppelReden = (typeof INKOOP_DOORBELASTING_TERUGKOPPEL_REDEN)[number]

export const INKOOP_DOORBELASTING_TERUGKOPPEL_REDEN_LABELS: Record<InkoopDoorbelastingTerugkoppelReden, string> = {
  VERKOOPFACTUUR_VERWIJDERD: 'Verkoopfactuur verwijderd',
  VERKOOPFACTUUR_GECREDITEERD: 'Verkoopfactuur gecrediteerd',
  VERKOOPREGEL_VERWIJDERD: 'Verkoopregel of werkbonmateriaal verwijderd',
  HANDMATIG: 'Handmatig teruggezet',
}

export const INKOOP_DOORBELASTING_SIGNAAL = [
  'BRON_GEWIJZIGD',
  'BRON_VERWIJDERD',
  'BRON_GECREDITEERD',
  'VERKOOP_DEELS_GECREDITEERD',
  // Review-fix: een herstelde verkoopfactuur of weer vervallen creditnota maakt
  // een doorbelasting weer actief terwijl de rest al opnieuw is doorbelast.
  'OVER_DOORBELAST',
] as const
export type InkoopDoorbelastingSignaal = (typeof INKOOP_DOORBELASTING_SIGNAAL)[number]

export const INKOOP_DOORBELASTING_SIGNAAL_LABELS: Record<InkoopDoorbelastingSignaal, string> = {
  BRON_GEWIJZIGD: 'Bron gewijzigd, controleer de verkoopregel',
  BRON_VERWIJDERD: 'Bron verwijderd, controleer de verkoopregel',
  BRON_GECREDITEERD: 'Bron gecrediteerd, controleer de verkoopregel',
  VERKOOP_DEELS_GECREDITEERD: 'Verkoopfactuur deels gecrediteerd',
  OVER_DOORBELAST: 'Meer doorbelast dan toegerekend, controleer de verkoopregels',
}

// ─── Toerekenen: "alle regels aan ..." ───────────────────────────────────────

/** Validatie: precies het doel-veld van het gekozen klant-doeltype is gevuld. */
function refineKlantDoel(
  d: {
    doeltype: KlantToewijzingDoeltype
    clientId?: string | null
    werkbonId?: string | null
    projectId?: string | null
  },
  ctx: z.RefinementCtx,
): void {
  const veld = d.doeltype === 'KLANT' ? 'clientId' : d.doeltype === 'WERKBON' ? 'werkbonId' : 'projectId'
  const waarde = d[veld]
  if (!waarde) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        d.doeltype === 'KLANT' ? 'Kies een klant.' : d.doeltype === 'WERKBON' ? 'Kies een werkbon.' : 'Kies een project.',
      path: [veld],
    })
  }
  for (const ander of ['clientId', 'werkbonId', 'projectId'] as const) {
    if (ander !== veld && d[ander]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies precies één doel: klant, werkbon of project.',
        path: [ander],
      })
    }
  }
}

/**
 * "Alle regels aan ..." (spec 183 §3.1 / T1): elke gekozen regel (leeg = alle
 * regels van de factuur) krijgt een toewijzing naar dit doel voor het deel dat
 * nog niet aan een klant is toegerekend. Een regel die al volledig aan klanten
 * hangt, wordt overgeslagen (niets wordt stil overschreven).
 */
export const inkoopToerekenenSchema = z
  .object({
    doeltype: z.enum(KLANT_TOEWIJZING_DOELTYPE),
    clientId: idParam('clientId').nullish(),
    werkbonId: idParam('werkbonId').nullish(),
    projectId: idParam('projectId').nullish(),
    regelIds: z.array(idParam('regelId')).max(500).optional(),
  })
  .superRefine(refineKlantDoel)
export type InkoopToerekenenInput = z.infer<typeof inkoopToerekenenSchema>

export interface InkoopToerekenenResultaatDTO {
  factuurId: string
  /** Regels die een (extra) toewijzing kregen. */
  toegerekend: number
  /** Regels die al volledig aan klanten hingen (overgeslagen). */
  overgeslagen: number
  /** Som van de nieuw toegerekende bedragen (excl. btw). */
  bedrag: number
}

// ─── Voorstellen (nooit automatisch koppelen) ────────────────────────────────

export const TOEREKENING_VOORSTEL_BRON = ['INKOOPORDER', 'KLANTREFERENTIE', 'GEHEUGEN'] as const
export type ToerekeningVoorstelBron = (typeof TOEREKENING_VOORSTEL_BRON)[number]

export const TOEREKENING_VOORSTEL_BRON_LABELS: Record<ToerekeningVoorstelBron, string> = {
  INKOOPORDER: 'Uit de inkooporder',
  KLANTREFERENTIE: 'Referentie op de factuur',
  GEHEUGEN: 'Op basis van eerdere keuze',
}

export interface ToerekeningVoorstelDTO {
  doeltype: KlantToewijzingDoeltype
  clientId: string | null
  werkbonId: string | null
  projectId: string | null
  /** "Pokon", "Werkbon 2026-0417", "Project P-0012 Uitbreiding hal 3". */
  doelNaam: string
  /** De klant achter het doel (ook bij werkbon/project). */
  klantId: string
  klantNaam: string
  bron: ToerekeningVoorstelBron
  bronLabel: string
  /** Korte uitleg, bv. "Inkooporder IO-2026-0012 is besteld voor werkbon 2026-0417". */
  toelichting: string
  zekerheid: 'HOOG' | 'MIDDEL'
}

export interface ToerekeningVoorstellenDTO {
  factuurId: string
  /** Voorstellen voor de hele factuur (inkooporder, referentie op de kop). */
  factuur: ToerekeningVoorstelDTO[]
  /** Per regel-id: voorstellen voor die regel (geheugen, referentie op de regel). */
  perRegel: Record<string, ToerekeningVoorstelDTO[]>
}

// ─── Doorbelasten ────────────────────────────────────────────────────────────

export const DOORBELAST_PRIJS_MODUS = ['OPSLAG', 'VAST'] as const
export type DoorbelastPrijsModus = (typeof DOORBELAST_PRIJS_MODUS)[number]

/**
 * Eén doorbelasting van (een deel van) een aan een klant toegerekende regel.
 * `opslagPct` is een FRACTIE (0.1 = 10%); de UI toont procenten en rekent om.
 */
export const inkoopDoorbelastenSchema = z
  .object({
    /** Welk klant-doel van de regel (de toewijzing). */
    doeltype: z.enum(KLANT_TOEWIJZING_DOELTYPE),
    doelId: idParam('doelId'),
    route: z.enum(['CONCEPT_FACTUUR', 'WERKBON_MATERIAAL']),
    /** CONCEPT_FACTUUR: een bestaande conceptfactuur; leeg = een nieuw concept. */
    invoiceId: idParam('invoiceId').nullish(),
    /** Nieuw concept: de factuurverzendende BV (voorgevuld met die van de inkoop). */
    entityId: idParam('entityId').nullish(),
    /** WERKBON_MATERIAAL: de werkbon (voorgevuld uit het doel). */
    werkbonId: idParam('werkbonId').nullish(),
    aantal: z.number().finite(),
    prijsModus: z.enum(DOORBELAST_PRIJS_MODUS),
    opslagPct: z.number().finite().min(-1).max(9.99, 'De opslag kan hoogstens 999% zijn.').nullish(),
    verkoopPrijsPerStuk: z.number().finite().nullish(),
    omschrijving: z.string().trim().min(1, 'Geef een omschrijving voor de klant.').max(500),
    /** Negatieve doorbelasting (creditregel) alleen na expliciete bevestiging (spec 183 §6.4). */
    bevestigNegatief: z.boolean().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.aantal === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Aantal mag niet 0 zijn.', path: ['aantal'] })
    }
    if (Math.abs(d.aantal * 100 - Math.round(d.aantal * 100)) > 1e-6) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Gebruik maximaal twee decimalen.', path: ['aantal'] })
    }
    if (d.prijsModus === 'OPSLAG' && (d.opslagPct === null || d.opslagPct === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Vul een opslagpercentage in.', path: ['opslagPct'] })
    }
    if (d.prijsModus === 'VAST' && (d.verkoopPrijsPerStuk === null || d.verkoopPrijsPerStuk === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Vul een verkoopprijs per stuk in.',
        path: ['verkoopPrijsPerStuk'],
      })
    }
    if (d.route === 'WERKBON_MATERIAAL' && !d.werkbonId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kies de werkbon.', path: ['werkbonId'] })
    }
  })
export type InkoopDoorbelastenInput = z.infer<typeof inkoopDoorbelastenSchema>

/** Bewust niet doorbelasten (met reden), of die keuze intrekken. */
export const inkoopNietDoorbelastenSchema = z
  .object({
    reden: z.enum(NIET_DOORBELASTEN_REDEN),
    toelichting: z.string().trim().max(500).nullish(),
  })
  .superRefine((d, ctx) => {
    if (d.reden === 'OVERIG' && !d.toelichting) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Licht de reden toe.',
        path: ['toelichting'],
      })
    }
  })
export type InkoopNietDoorbelastenInput = z.infer<typeof inkoopNietDoorbelastenSchema>

/** Opslag-instelling per klant (fractie; null = de globale default). */
export const clientDoorbelastOpslagSchema = z.object({
  doorbelastOpslagPct: z.number().finite().min(0).max(9.99, 'De opslag kan hoogstens 999% zijn.').nullable(),
})
export type ClientDoorbelastOpslagInput = z.infer<typeof clientDoorbelastOpslagSchema>

/** Eén doorbelasting (read-vorm, inkoopkant en werkvoorraad). */
export interface InkoopDoorbelastingDTO {
  id: string
  inkoopFactuurId: string
  inkoopFactuurRegelId: string
  route: InkoopDoorbelastingRoute
  routeLabel: string
  doeltype: ToewijzingDoeltype
  clientId: string
  klantNaam: string | null
  werkbonId: string | null
  werkbonNummer: string | null
  projectId: string | null
  projectNummer: string | null
  /** Verkoopfactuur (concept of definitief), zodra bekend. */
  invoiceId: string | null
  invoiceNummer: string | null
  invoiceStatus: string | null
  invoiceLineId: string | null
  werkbonMaterialId: string | null
  finqleBillingRequestId: string | null
  aantal: number
  /**
   * Het doorbelaste deel van de inkoopkosten (excl. btw). Review-fix: alleen met
   * margerecht (`canViewMargins`); anders null, want inkoop naast verkoop IS de
   * marge.
   */
  inkoopBedrag: number | null
  /** Opslag of vaste verkoopprijs (altijd zichtbaar; zegt niets over de marge). */
  prijsModus: DoorbelastPrijsModus
  /** Opslag als fractie (0.1 = 10%); null bij een vaste verkoopprijs of zonder margerecht. */
  opslagPct: number | null
  verkoopPrijsPerStuk: number
  verkoopBedrag: number
  /**
   * Marge (verkoop - inkoop). Alleen gevuld voor rollen met margerecht
   * (`canViewMargins`); anders null. Nooit naar klantportaal of ACCOUNTANT.
   */
  marge: number | null
  omschrijving: string
  status: InkoopDoorbelastingStatus
  teruggekoppeldOp: string | null
  terugkoppelReden: InkoopDoorbelastingTerugkoppelReden | null
  terugkoppelRedenLabel: string | null
  signaal: InkoopDoorbelastingSignaal | null
  signaalLabel: string | null
  signaalOp: string | null
  signaalAfgehandeldOp: string | null
  createdAt: string
  createdByNaam: string | null
}

/** Opslag-instelling van één klant: eigen opslag (fractie) of de globale terugval. */
export interface ClientDoorbelastOpslagDTO {
  doorbelastOpslagPct: number | null
  standaardOpslagPct: number
}

/** Een doorbelasting zoals de verkoopfactuur hem toont (met de inkoopbron). */
export interface DoorbelasteInkoopRegelDTO extends InkoopDoorbelastingDTO {
  inkoopFactuurNr: string
  leverancierNaam: string
}

/** Eén klant-doel van een regel in de doorbelast-dialoog. */
export interface DoorbelastDoelOptieDTO {
  doeltype: KlantToewijzingDoeltype
  doelId: string
  doelNaam: string
  klantId: string
  klantNaam: string
  /** Toegerekend bedrag (excl. btw) en het aantal-equivalent daarvan. */
  toegerekend: number
  aantalToegerekend: number
  /** Al doorbelast (actieve doorbelastingen, inkoopkant). */
  doorbelast: number
  aantalDoorbelast: number
  /** Nog te doorbelasten. */
  rest: number
  aantalRest: number
  /** Opslag (fractie) die voorgesteld wordt: die van de klant, anders de globale. */
  opslagPct: number
  opslagBron: 'KLANT' | 'GLOBAAL'
  /** Bestaande conceptfacturen van deze klant (eigen facturatie). */
  conceptFacturen: DoorbelastConceptFactuurDTO[]
  /**
   * Review-fix (geen stille afkap): het totaal aantal open concepten van deze
   * klant; `conceptFacturen` toont de recentste (maximaal 20).
   */
  conceptFacturenTotaal: number
  /** Mag STREEP zelf een factuur maken voor deze klant (niet bij de Finqle-API-methode)? */
  eigenFactuurToegestaan: boolean
  eigenFactuurMelding: string | null
  /** Facturatie via Finqle: doorbelasten gaat via het werkbonmateriaal (FO-470). */
  viaFinqle: boolean
  /** De werkbon (bij doeltype WERKBON): kan er nog materiaal bij? */
  werkbon: {
    id: string
    nummer: string
    materiaalToevoegbaar: boolean
    reden: string | null
  } | null
  /** BV-mismatch: inkoop op een andere BV dan de verkoop (intercompany-hint, §6.2). */
  intercompanyHint: string | null
}

export interface DoorbelastConceptFactuurDTO {
  id: string
  nummer: string
  entityId: string
  entityNaam: string | null
  datum: string | null
  totaalExclBtw: number
  aantalRegels: number
}

export interface DoorbelastOptiesDTO {
  regelId: string
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  omschrijving: string
  aantal: number
  /** Inkoopprijs per stuk (excl. btw), afgeleid uit regeltotaal / aantal. */
  prijsPerStuk: number
  totaal: number
  /** BV van de inkoopfactuur (voorvulling van een nieuw concept). */
  entityId: string | null
  entityNaam: string | null
  doelen: DoorbelastDoelOptieDTO[]
  doorbelastingen: InkoopDoorbelastingDTO[]
  nietDoorbelastenReden: NietDoorbelastenReden | null
  nietDoorbelastenToelichting: string | null
}

export interface InkoopDoorbelastenResultaatDTO {
  doorbelasting: InkoopDoorbelastingDTO
  /** De nieuwe doorbelast-status van de inkoopregel. */
  doorbelastStatus: InkoopDoorbelastStatus | null
}

// ─── Werkvoorraad "te doorbelasten" ──────────────────────────────────────────

export const TE_DOORBELASTEN_TAB = ['TE_DOORBELASTEN', 'NAGEKOMEN', 'SIGNALEN', 'NIET'] as const
export type TeDoorbelastenTab = (typeof TE_DOORBELASTEN_TAB)[number]

export const TE_DOORBELASTEN_TAB_LABELS: Record<TeDoorbelastenTab, string> = {
  TE_DOORBELASTEN: 'Te doorbelasten',
  NAGEKOMEN: 'Nagekomen kosten',
  SIGNALEN: 'Signalen',
  NIET: 'Niet doorbelast',
}

export const teDoorbelastenQuerySchema = z.object({
  tab: z.enum(TE_DOORBELASTEN_TAB).default('TE_DOORBELASTEN'),
  clientId: idParam('clientId').optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type TeDoorbelastenQuery = z.infer<typeof teDoorbelastenQuerySchema>

/** Eén klant-doel van een regel dat nog (deels) doorbelast moet worden. */
export interface TeDoorbelastenDoelDTO {
  doeltype: KlantToewijzingDoeltype
  doelId: string
  doelNaam: string
  klantId: string
  klantNaam: string
  toegerekend: number
  doorbelast: number
  rest: number
  /** De werkbon is al gefactureerd: nagekomen kosten (spec 183 §6.5). */
  nagekomen: boolean
}

export interface TeDoorbelastenRegelDTO {
  regelId: string
  factuurId: string
  factuurNr: string
  /** Factuurdatum (yyyy-MM-dd, Europe/Amsterdam). */
  factuurdatum: string
  leverancierNaam: string
  omschrijving: string
  kostensoortNaam: string | null
  totaal: number
  status: InkoopDoorbelastStatus | null
  /** Dagen sinds de huidige status (ouderdom). */
  dagenOud: number | null
  doelen: TeDoorbelastenDoelDTO[]
  nietDoorbelastenReden: NietDoorbelastenReden | null
  nietDoorbelastenRedenLabel: string | null
  nietDoorbelastenToelichting: string | null
  /** Laatste terugkoppeling (verkoopfactuur verwijderd/gecrediteerd), als die er is. */
  teruggekoppeld: { reden: string; op: string } | null
}

export interface TeDoorbelastenOverzichtDTO {
  tab: TeDoorbelastenTab
  regels: TeDoorbelastenRegelDTO[]
  /** Alleen op de tab SIGNALEN. */
  signalen: (InkoopDoorbelastingDTO & { factuurNr: string; regelOmschrijving: string })[]
  /** Tellers per tab, server-side over de volledige set. */
  tellers: Record<TeDoorbelastenTab, number>
  meta: { total: number; page: number; pageSize: number }
}
